import { useLayoutEffect, useRef } from 'react';
import type { ComponentProps, ReactNode, RefObject } from 'react';
import { cn } from '../lib/cn.ts';
import { TabsContent as ContentPrimitive, TabsList as ListPrimitive, Tabs as TabsPrimitive, TabsTrigger as TriggerPrimitive } from '../primitives/tabs.tsx';

/**
 * Tabs switch between different content on one page, on the shadcn Tabs (components.md): 6 or
 * fewer, 1 to 2 word labels, the selected tab in weight 500 over one sliding Prism ray indicator. Keep the tab in
 * the URL (`?tab=`). A view switch of the same content is a Segmented.
 */
export function Tabs({ className, ...rest }: ComponentProps<typeof TabsPrimitive>) {
  return <TabsPrimitive className={cn('block', className)} {...rest} />;
}

/**
 * The classes a horizontal scroller wears with `useScrollEdges`: the edge that hides more options
 * fades out, so a row cut at the panel edge reads as "more this way", never as a clipped label.
 */
export const SCROLL_EDGE_FADE = 'data-fade-start:mask-l-from-85% data-fade-end:mask-r-from-85%';

/** The breathing room left beside an option scrolled into view, so its neighbour peeks out. */
const REVEAL_PAD = 24;

/**
 * Keeps a horizontal scroller's edges marked: `data-fade-start` while content hides to the left,
 * `data-fade-end` while it hides to the right (style them with SCROLL_EDGE_FADE). With `active`,
 * the option matching that selector is scrolled into view inside the scroller only, whenever it
 * changes, so no ancestor (a drawer body, the page) ever scrolls sideways to reveal it.
 */
export function useScrollEdges(ref: RefObject<HTMLElement | null>, active?: string) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    return watchScrollEdges(el, active);
  }, [ref, active]);
}

/** useScrollEdges' work on one element; returns the cleanup. */
export function watchScrollEdges(el: HTMLElement, active?: string): () => void {
  const mark = () => {
    const hidden = el.scrollWidth - el.clientWidth;
    el.toggleAttribute('data-fade-start', el.scrollLeft > 1);
    el.toggleAttribute('data-fade-end', hidden - el.scrollLeft > 1);
  };
  const reveal = () => {
    if (!active) return;
    const item = el.querySelector<HTMLElement>(active);
    if (!item || el.scrollWidth <= el.clientWidth) return;
    const start = item.offsetLeft;
    const end = start + item.offsetWidth;
    let left = el.scrollLeft;
    if (start - REVEAL_PAD < left) left = Math.max(0, start - REVEAL_PAD);
    else if (end + REVEAL_PAD > left + el.clientWidth) left = end + REVEAL_PAD - el.clientWidth;
    if (left !== el.scrollLeft) el.scrollLeft = left;
  };
  el.addEventListener('scroll', mark, { passive: true });
  const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
    reveal();
    mark();
  });
  // The observer's first call comes after layout, before paint. Reading the widths here, in
  // the commit, forced the whole new page to lay out inside it: 200 to 500 ms of every page
  // open at scale went to a Segmented's first look (22 Oct 2026). Without one, read now.
  if (!ro) {
    reveal();
    mark();
  }
  ro?.observe(el);
  for (const child of Array.from(el.children)) ro?.observe(child);
  const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => {
    reveal();
    mark();
  });
  mo?.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-state'] });
  return () => {
    el.removeEventListener('scroll', mark);
    ro?.disconnect();
    mo?.disconnect();
  };
}

/**
 * The underline tab row. It owns its horizontal overflow: a row wider than its column scrolls
 * inside itself with a fade on the hidden edge, and the active tab scrolls into view within the
 * row, so the panel around it never scrolls sideways (a drawer body keeps its left edge).
 */
export function TabsList({ className, children, style, ...rest }: ComponentProps<typeof ListPrimitive>) {
  const probe = useRef<HTMLSpanElement>(null);
  const list = useRef<HTMLElement | null>(null);
  // The primitive keeps its own ref for the sliding indicator, so the row is found from a probe inside it.
  useLayoutEffect(() => {
    list.current = probe.current?.parentElement ?? null;
  }, []);
  useScrollEdges(list, '[role="tab"][data-state="active"]');
  return (
    <ListPrimitive
      variant="line"
      className={cn(
        'relative max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain',
        // Inside a scroller the indicator sits on the row's line rather than over it, or it would be clipped.
        '[&>[data-slot=tabs-indicator]]:bottom-0',
        SCROLL_EDGE_FADE,
        className,
      )}
      style={{ scrollbarWidth: 'none', ...style }}
      {...rest}
    >
      <span ref={probe} aria-hidden hidden />
      {children}
    </ListPrimitive>
  );
}

export interface TabsTriggerProps extends ComponentProps<typeof TriggerPrimitive> {
  /** A count shown after the label. */
  count?: number;
  icon?: ReactNode;
}

/**
 * One tab. Only the selected tab is bright and medium weight: hover lifts an unselected label one
 * step (to the muted tone), and focus shows only the ring, so a hovered or last-pressed tab never
 * reads as a second selection.
 */
export function TabsTrigger({ className, count, icon, children, ...rest }: TabsTriggerProps) {
  return (
    <TriggerPrimitive className={cn('shrink-0 font-normal hover:text-fg-muted data-[state=active]:font-medium data-[state=active]:hover:text-fg', className)} {...rest}>
      {icon}
      {children}
      {count != null && (
        <span className="inline-flex h-(--pill-height) min-w-(--pill-height) items-center justify-center rounded-pill bg-fg/6 px-1.5 text-caption font-medium text-fg-muted num">{count}</span>
      )}
    </TriggerPrimitive>
  );
}

export function TabsContent({ className, ...rest }: ComponentProps<typeof ContentPrimitive>) {
  return <ContentPrimitive className={className} {...rest} />;
}
